<template>
  <div class="pc-login-page">
    <van-nav-bar title="登录 PC 工作台" left-text="返回" left-arrow fixed placeholder @click-left="goBack" />

    <main class="login-content">
      <section class="intro-card">
        <div class="device-art">
          <div class="screen"><span>票天下</span></div>
          <div class="phone"><van-icon name="scan" /></div>
        </div>
        <h1>扫码登录同一账号</h1>
        <p>打开 PC 登录页上的二维码，在这里扫一扫并确认，无需输入密码。</p>
        <div class="account-chip">
          <van-icon name="user-circle-o" />
          <span>将登录账号：{{ accountName }}</span>
        </div>
      </section>

      <section class="pc-address-card">
        <div class="pc-address-heading">
          <div>
            <strong>PC 登录地址</strong>
            <p>复制后在电脑浏览器中打开</p>
          </div>
          <van-icon name="desktop-o" />
        </div>
        <div class="pc-address-row">
          <span>{{ loginUrl }}</span>
          <van-button size="small" type="primary" :loading="copyingUrl" @click="copyUrl">复制</van-button>
        </div>
      </section>

      <van-button class="scan-button" type="primary" block :loading="confirming" @click="openScanner">
        <van-icon name="scan" />
        扫一扫登录 PC
      </van-button>

      <section class="steps-card">
        <div class="section-title">操作步骤</div>
        <div class="step"><span>1</span><p>在电脑打开票天下接单系统登录页</p></div>
        <div class="step"><span>2</span><p>保持“扫码登录”二维码显示在屏幕上</p></div>
        <div class="step"><span>3</span><p>点击上方按钮扫码，核对设备后确认登录</p></div>
      </section>

      <section class="fallback-card">
        <div class="fallback-heading">
          <div>
            <strong>无法扫码？</strong>
            <p>可在 PC 端切换到“动态码登录”</p>
          </div>
          <van-button size="small" plain type="primary" :loading="refreshing" @click="generateCode">刷新</van-button>
        </div>
        <div class="code-row">
          <div>
            <span>移动端账号</span>
            <strong>{{ userInfo.mobile || userInfo.name || userInfo.username || '—' }}</strong>
          </div>
          <div class="code-value" @click="copyCode">
            <span>6位动态码</span>
            <strong>{{ code || '------' }}</strong>
          </div>
        </div>
        <p class="expire-tip"><van-icon name="clock-o" /> 动态码 5 分钟有效，请勿发送给他人</p>
      </section>
    </main>

    <div v-if="showScanner" class="scanner-layer">
      <video ref="video" autoplay muted playsinline webkit-playsinline />
      <canvas ref="canvas" class="scanner-canvas" />
      <div class="scanner-shade">
        <div class="scanner-top">
          <button type="button" @click="closeScanner"><van-icon name="cross" /></button>
          <strong>扫描 PC 登录二维码</strong>
          <span />
        </div>
        <div class="scan-frame"><i /></div>
        <p>{{ scannerTip }}</p>
        <label class="photo-fallback">
          从相册识别
          <input type="file" accept="image/*" @change="decodeImageFile">
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import jsQR from 'jsqr'
import Api from '@/api-service'

const QR_PREFIX = 'DDCINEMA_PC_LOGIN'
const DEFAULT_PC_LOGIN_URL = 'http://merchant.24xiaoshi.online/'

export default {
  name: 'PCLogin',
  data() {
    return {
      loginUrl: DEFAULT_PC_LOGIN_URL,
      code: '',
      copyingUrl: false,
      refreshing: false,
      confirming: false,
      showScanner: false,
      scannerTip: '将 PC 端二维码放入框内',
      mediaStream: null,
      animationFrame: null,
      scanLocked: false
    }
  },
  computed: {
    userInfo() {
      return this.$store.state.user.userInfo || {}
    },
    accountName() {
      return this.userInfo.nickName || this.userInfo.nick_name || this.userInfo.mobile || this.userInfo.username || '当前出票商账号'
    }
  },
  created() {
    this.generateCode()
  },
  beforeDestroy() {
    this.stopCamera()
  },
  methods: {
    goBack() {
      this.$router.back()
    },
    async generateCode() {
      this.refreshing = true
      try {
        const result = await Api.makeCode({})
        this.code = result.code || ''
        this.loginUrl = result.url || DEFAULT_PC_LOGIN_URL
      } catch (error) {
        this.$toast.fail('动态码生成失败')
      } finally {
        this.refreshing = false
      }
    },
    async copyUrl() {
      this.copyingUrl = true
      try {
        await this.copyText(this.loginUrl)
        this.$toast.success('PC 登录地址已复制')
      } catch (error) {
        this.$toast.fail('复制失败，请长按地址复制')
      } finally {
        this.copyingUrl = false
      }
    },
    async copyCode() {
      if (!this.code) return
      try {
        await this.copyText(this.code)
        this.$toast.success('动态码已复制')
      } catch (error) {
        this.$toast.fail('复制失败，请手动输入')
      }
    },
    async copyText(text) {
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(text)
          return
        } catch (error) {
          // 部分 App WebView 暴露了 clipboard API，但实际调用会被拒绝，继续使用兼容方案。
        }
      }
      const input = document.createElement('textarea')
      input.value = text
      input.setAttribute('readonly', '')
      input.style.position = 'fixed'
      input.style.opacity = '0'
      document.body.appendChild(input)
      input.select()
      input.setSelectionRange(0, input.value.length)
      let copied = false
      try {
        copied = document.execCommand('copy')
      } finally {
        document.body.removeChild(input)
      }
      if (!copied) throw new Error('copy failed')
    },
    async openScanner() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.$toast.fail('当前浏览器无法调用相机，请从相册识别')
        this.showScanner = true
        return
      }
      this.showScanner = true
      this.scanLocked = false
      this.scannerTip = '正在打开相机…'
      await this.$nextTick()
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: 'environment' } }
        })
        const video = this.$refs.video
        video.srcObject = this.mediaStream
        await video.play()
        this.scannerTip = '将 PC 端二维码放入框内'
        this.scanFrame()
      } catch (error) {
        this.scannerTip = '相机打开失败，可从相册选择二维码截图'
        this.$toast.fail('请允许相机权限，或从相册识别')
      }
    },
    scanFrame() {
      if (!this.showScanner || this.scanLocked) return
      const video = this.$refs.video
      const canvas = this.$refs.canvas
      if (video && canvas && video.readyState >= 2 && video.videoWidth) {
        const width = Math.min(video.videoWidth, 720)
        const height = Math.round(width * video.videoHeight / video.videoWidth)
        canvas.width = width
        canvas.height = height
        const context = canvas.getContext('2d', { willReadFrequently: true })
        context.drawImage(video, 0, 0, width, height)
        const imageData = context.getImageData(0, 0, width, height)
        const result = jsQR(imageData.data, width, height, { inversionAttempts: 'attemptBoth' })
        if (result && result.data) {
          this.scanLocked = true
          this.handleQRContent(result.data)
          return
        }
      }
      this.animationFrame = window.requestAnimationFrame(this.scanFrame)
    },
    async decodeImageFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      try {
        const image = await this.readImage(file)
        const canvas = this.$refs.canvas
        const maxWidth = 1200
        const scale = Math.min(1, maxWidth / image.width)
        canvas.width = Math.round(image.width * scale)
        canvas.height = Math.round(image.height * scale)
        const context = canvas.getContext('2d', { willReadFrequently: true })
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
        const result = jsQR(imageData.data, canvas.width, canvas.height, { inversionAttempts: 'attemptBoth' })
        if (!result) throw new Error('not found')
        this.scanLocked = true
        this.handleQRContent(result.data)
      } catch (error) {
        this.scanLocked = false
        this.$toast.fail('未识别到有效的 PC 登录二维码')
      }
    },
    readImage(file) {
      return new Promise((resolve, reject) => {
        const image = new Image()
        const url = URL.createObjectURL(file)
        image.onload = () => {
          URL.revokeObjectURL(url)
          resolve(image)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('image load failed'))
        }
        image.src = url
      })
    },
    parseQRContent(content) {
      const parts = String(content || '').trim().split('|')
      if (parts.length !== 4 || parts[0] !== QR_PREFIX || parts[1] !== '1') return null
      if (!/^[a-f0-9]{32}$/.test(parts[2]) || !/^[a-f0-9]{64}$/.test(parts[3])) return null
      return { session_id: parts[2], approval_token: parts[3] }
    },
    async handleQRContent(content) {
      const loginData = this.parseQRContent(content)
      if (!loginData) {
        this.scanLocked = false
        this.scannerTip = '不是有效的票天下 PC 登录二维码'
        this.$toast.fail(this.scannerTip)
        this.animationFrame = window.requestAnimationFrame(this.scanFrame)
        return
      }
      this.stopCamera()
      this.showScanner = false
      try {
        await this.$dialog.confirm({
          title: '确认登录 PC？',
          message: `将使用“${this.accountName}”登录电脑端。请确认二维码来自您正在操作的设备。`,
          confirmButtonText: '确认登录',
          cancelButtonText: '取消'
        })
      } catch (error) {
        this.scanLocked = false
        return
      }
      this.confirming = true
      try {
        await Api.confirmPCLogin(loginData)
        this.$dialog.alert({
          title: '登录成功',
          message: 'PC 端已登录当前账号，可以回到电脑继续操作。',
          confirmButtonText: '知道了'
        })
      } catch (error) {
        this.$toast.fail('确认失败，二维码可能已过期')
      } finally {
        this.confirming = false
        this.scanLocked = false
      }
    },
    stopCamera() {
      window.cancelAnimationFrame(this.animationFrame)
      this.animationFrame = null
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop())
        this.mediaStream = null
      }
      const video = this.$refs.video
      if (video) video.srcObject = null
    },
    closeScanner() {
      this.stopCamera()
      this.showScanner = false
      this.scanLocked = false
    }
  }
}
</script>

<style lang="scss" scoped>
.pc-login-page { min-height: 100vh; background: #f5f6fa; color: #26293d; }
.login-content { padding: 16px 16px 34px; }

.intro-card, .pc-address-card, .steps-card, .fallback-card {
  margin-bottom: 14px;
  padding: 22px 18px;
  border: 1px solid #eaebf1;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 7px 26px rgba(35, 39, 75, .05);
}

.intro-card { text-align: center; }
.device-art { position: relative; width: 126px; height: 82px; margin: 0 auto 18px; }
.screen { display: grid; width: 112px; height: 70px; place-items: center; border: 5px solid #2f325b; border-radius: 10px; color: #fff; background: linear-gradient(135deg, #5c60d2, #34376f); font-size: 13px; font-weight: 800; letter-spacing: 1px; }
.screen::after { position: absolute; bottom: 0; left: 30px; width: 52px; height: 5px; border-radius: 5px; background: #2f325b; content: ''; }
.phone { position: absolute; right: 0; bottom: 2px; display: grid; width: 38px; height: 58px; place-items: center; border: 3px solid #262947; border-radius: 9px; color: #5559c9; background: #fff; font-size: 22px; box-shadow: -5px 4px 12px rgba(28, 31, 62, .18); }
.intro-card h1 { margin: 0 0 8px; font-size: 22px; }
.intro-card > p { margin: 0 auto; max-width: 280px; color: #858999; font-size: 13px; line-height: 1.65; }
.account-chip { display: inline-flex; align-items: center; gap: 6px; margin-top: 15px; padding: 7px 11px; border-radius: 20px; color: #5155b7; background: #eff0ff; font-size: 12px; }

.pc-address-heading { display: flex; align-items: center; justify-content: space-between; }
.pc-address-heading strong { font-size: 15px; }
.pc-address-heading p { margin: 5px 0 0; color: #999ca8; font-size: 12px; }
.pc-address-heading > .van-icon { color: #5559c9; font-size: 25px; }
.pc-address-row { display: flex; align-items: center; gap: 10px; margin-top: 15px; padding: 10px 10px 10px 13px; border-radius: 12px; background: #f6f6f9; }
.pc-address-row span { min-width: 0; flex: 1; color: #4b4f68; font-size: 13px; line-height: 1.45; word-break: break-all; user-select: text; }
.pc-address-row .van-button { flex: none; border-radius: 9px; }

.scan-button { height: 51px; margin: 2px 0 14px; border: 0; border-radius: 14px; background: linear-gradient(135deg, #5559ca, #45489d); box-shadow: 0 10px 24px rgba(74, 78, 177, .28); font-size: 16px; font-weight: 600; }
.scan-button .van-icon { margin-right: 7px; font-size: 20px; vertical-align: -3px; }

.section-title { margin-bottom: 16px; font-size: 16px; font-weight: 650; }
.step { display: flex; align-items: center; gap: 12px; margin-top: 13px; }
.step span { display: grid; width: 25px; height: 25px; flex: none; place-items: center; border-radius: 8px; color: #5559c5; background: #eeefff; font-size: 12px; font-weight: 700; }
.step p { margin: 0; color: #5e6273; font-size: 13px; }

.fallback-heading, .code-row { display: flex; align-items: center; justify-content: space-between; }
.fallback-heading strong { font-size: 15px; }
.fallback-heading p { margin: 5px 0 0; color: #999ca8; font-size: 12px; }
.fallback-heading .van-button { border-radius: 9px; }
.code-row { gap: 10px; margin-top: 18px; padding: 14px; border-radius: 12px; background: #f6f6f9; }
.code-row > div { min-width: 0; }
.code-row span { display: block; margin-bottom: 5px; color: #9699a6; font-size: 11px; }
.code-row strong { display: block; overflow: hidden; color: #33364d; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }
.code-value { flex: none; text-align: right; }
.code-value strong { color: #5155bd; font-size: 23px; letter-spacing: 3px; }
.expire-tip { margin: 12px 0 0; color: #aaaeba; font-size: 11px; text-align: center; }

.scanner-layer { position: fixed; z-index: 9999; inset: 0; overflow: hidden; background: #080912; }
.scanner-layer video { width: 100%; height: 100%; object-fit: cover; }
.scanner-canvas { display: none; }
.scanner-shade { position: absolute; inset: 0; display: flex; align-items: center; flex-direction: column; color: #fff; background: radial-gradient(circle at center, transparent 0, transparent 132px, rgba(7, 8, 17, .64) 134px); }
.scanner-top { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: calc(14px + env(safe-area-inset-top)) 18px 14px; box-sizing: border-box; }
.scanner-top button, .scanner-top span { width: 38px; }
.scanner-top button { height: 38px; padding: 0; border: 0; border-radius: 50%; color: #fff; background: rgba(0, 0, 0, .36); font-size: 20px; }
.scan-frame { position: relative; width: 250px; height: 250px; margin-top: 12vh; border: 2px solid rgba(255, 255, 255, .85); border-radius: 18px; box-shadow: 0 0 0 1px rgba(87, 232, 201, .5); }
.scan-frame i { position: absolute; top: 12px; right: 12px; left: 12px; height: 2px; background: #62e1c4; box-shadow: 0 0 10px #62e1c4; animation: scan-line 2s ease-in-out infinite; }
.scanner-shade > p { margin-top: 25px; font-size: 14px; }
.photo-fallback { margin-top: 12px; padding: 9px 16px; border: 1px solid rgba(255, 255, 255, .45); border-radius: 20px; font-size: 13px; }
.photo-fallback input { display: none; }

@keyframes scan-line { 0%, 100% { transform: translateY(0); opacity: .45; } 50% { transform: translateY(222px); opacity: 1; } }
</style>
